/**
 * Catchball Tier Turn Store
 *
 * Manages org-unit-specific ball position tracking within Catchball loops.
 *
 * This store is the authoritative runtime answer to:
 *   "Who has the ball right now?"  → activePosition per loop
 *   "What does this level need to do?" → judgmentPrerequisites on the active turn
 *   "Who has visibility?"            → involvements on the active turn
 *
 * Build Contract §2.3: DB is the system of record. No localStorage.
 * Build Contract §2.3a: All mutations go through transactional RPCs.
 *
 * Schema:  supabase/migrations/224_catchball_level_turns.sql
 * Types:   src/types/prioritization/catchballTierTurn.ts
 *
 * RPCs
 *   catchball_pass_ball                   — move ball one level (down or up)
 *   catchball_involve_level               — ball holder involves the level below
 *   catchball_mark_prerequisite_satisfied — judgment prereq marked done
 *   catchball_deliver_involvement         — delegate-mode output delivered
 *
 * Read path: direct table select + active_ball_position view
 */

import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  getSafeSupabaseClient,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import type {
  CatchballTierTurn,
  CatchballTierInvolvement,
  CatchballLoopLevelState,
  ActiveBallPosition,
  PassBallParams,
  InvolveLevelParams,
  MarkPrerequisiteSatisfiedParams,
  DeliverInvolvementParams,
  InitiateTurnParams,
  FanOutParams,
  FanOutInvalidTarget,
  OriginateLoopParams,
  JudgmentPrerequisite,
  PrerequisiteSatisfactionMap,
} from '../../types/prioritization/catchballTierTurn';
import type { AggregateBallsParams } from '../../types/prioritization/catchballPathInstance';
import { useCatchballBallStore } from './catchballBallStore';
import { useCatchballLoopStore } from './catchballLoopStore';

// ============================================================================
// DB → TS converters
// ============================================================================

function dbRowToTurn(row: Record<string, unknown>): CatchballTierTurn {
  return {
    id:                       row.id as string,
    loopId:                   row.loop_id as string,
    workspaceId:              (row.workspace_id as string | null) ?? undefined,
    ballId:                   (row.ball_id as string | null) ?? null,
    orgUnitId:                row.org_unit_id as string,
    ballDirection:            row.ball_direction as CatchballTierTurn['ballDirection'],
    state:                    row.state as CatchballTierTurn['state'],
    receivedFromOrgUnitId:    (row.received_from_org_unit_id as string | null) ?? undefined,
    receivedAt:               row.received_at as string,
    receivedContent:          (row.received_content as Record<string, unknown> | null) ?? undefined,
    judgmentPrerequisites:    (row.judgment_prerequisites as JudgmentPrerequisite[] | null) ?? [],
    prerequisiteSatisfaction: (row.prerequisite_satisfaction as PrerequisiteSatisfactionMap | null) ?? {},
    passedAt:                 (row.passed_at as string | null) ?? undefined,
    passedToOrgUnitId:        (row.passed_to_org_unit_id as string | null) ?? undefined,
    passedBy:                 (row.passed_by as string | null) ?? undefined,
    passedContent:            (row.passed_content as Record<string, unknown> | null) ?? undefined,
    sequenceNumber:           row.sequence_number as number,
    createdAt:                row.created_at as string,
    createdBy:                (row.created_by as string | null) ?? undefined,
  };
}

function dbRowToInvolvement(row: Record<string, unknown>): CatchballTierInvolvement {
  return {
    id:                row.id as string,
    loopId:            row.loop_id as string,
    tierTurnId:       row.level_turn_id as string,
    workspaceId:       (row.workspace_id as string | null) ?? undefined,
    holderOrgUnitId:   row.holder_org_unit_id as string,
    targetOrgUnitId:   row.target_org_unit_id as string,
    involvementMode:   row.involvement_mode as CatchballTierInvolvement['involvementMode'],
    requestContent:    (row.request_content as Record<string, unknown> | null) ?? undefined,
    state:             row.state as CatchballTierInvolvement['state'],
    deliveredAt:       (row.delivered_at as string | null) ?? undefined,
    deliveredContent:  (row.delivered_content as Record<string, unknown> | null) ?? undefined,
    deliveredBy:       (row.delivered_by as string | null) ?? undefined,
    createdAt:         row.created_at as string,
    createdBy:         (row.created_by as string | null) ?? undefined,
  };
}

function dbRowToActivePosition(row: Record<string, unknown>): ActiveBallPosition {
  return {
    loopId:               row.loop_id as string,
    turnId:               row.turn_id as string,
    ballHolderOrgUnitId:  row.ball_holder_org_unit_id as string,
    ballDirection:        row.ball_direction as ActiveBallPosition['ballDirection'],
    state:                row.state as ActiveBallPosition['state'],
    receivedAt:           row.received_at as string,
    sequenceNumber:       row.sequence_number as number,
    ballId:               (row.ball_id as string | null) ?? null,
  };
}

// ============================================================================
// Store interface
// ============================================================================

interface CatchballTierTurnState {
  /** Level state keyed by loop ID */
  byLoop: Record<string, CatchballLoopLevelState>;
  loading: Record<string, boolean>;
  error: Record<string, string | null>;

  /**
   * Realtime channel + the loop it currently watches. `catchball_level_turns`
   * has no `cycle_id` column (only `loop_id`), so the subscription is per-loop,
   * matching the workbench's one-loop-at-a-time view. Mirrors catchballLoopStore's
   * `_realtimeChannel`/`_realtimeCycleId` slots.
   */
  _realtimeChannel: RealtimeChannel | null;
  _realtimeLoopId: string | null;

  // ── Read ──────────────────────────────────────────────────────────────────

  /**
   * Load all turns, involvements, and the active ball position for a loop.
   * No-ops if already loaded and force is false.
   */
  loadLoop: (loopId: string, force?: boolean) => Promise<void>;

  /** Returns the full level state for a loop, or null if not yet loaded. */
  getLoopState: (loopId: string) => CatchballLoopLevelState | null;

  /** Returns the primary active ball position for a loop (first branch). */
  getActivePosition: (loopId: string) => ActiveBallPosition | null;

  /**
   * Returns one active ball position per parallel branch (from
   * catchball_active_branch_positions). Empty or single for single-ball loops.
   */
  getActivePositions: (loopId: string) => ActiveBallPosition[];

  /**
   * Returns all turns for a specific org unit within a loop, ordered by sequence.
   * Useful for showing a level's history within one loop pass.
   */
  getTurnsForOrgUnit: (loopId: string, orgUnitId: string) => CatchballTierTurn[];

  /**
   * Returns the current active turn for an org unit in a loop, if any.
   * This is the turn that has the ball right now.
   */
  getActiveTurnForOrgUnit: (loopId: string, orgUnitId: string) => CatchballTierTurn | null;

  /** Returns involvements for a specific turn. */
  getInvolvementsForTurn: (turnId: string) => CatchballTierInvolvement[];

  /**
   * Fetches all active (state = 'active' | 'ready_to_pass') turns for an org unit
   * across ALL loops, then loads each loop's full state into the store.
   *
   * Used to answer: "Does my level have the ball anywhere right now?"
   */
  fetchActiveForOrgUnit: (orgUnitId: string) => Promise<Array<{
    loopId: string;
    cycleId: string;
    turn: CatchballTierTurn;
  }>>;

  /**
   * Fetches all active turns for a user, by first resolving ALL of their
   * org unit memberships from user_node_assignments, then querying across
   * all of those units. Covers multi-level org membership.
   *
   * Use this in My Tasks instead of fetchActiveForOrgUnit to ensure the
   * query doesn't silently fail when the user is in multiple org units.
   */
  fetchActiveForUser: (userId: string) => Promise<Array<{
    loopId: string;
    cycleId: string;
    turn: CatchballTierTurn;
  }>>;

  /**
   * Returns open involvements where the given org unit is the target.
   * Used by a level to see what involvement requests they've received.
   */
  getInvolvementsTargeting: (loopId: string, targetOrgUnitId: string) => CatchballTierInvolvement[];

  isLoading: (loopId: string) => boolean;
  getError: (loopId: string) => string | null;

  // ── Mutations (§2.3a — via RPC) ───────────────────────────────────────────

  /**
   * Create the first tier turn for a loop (direction = 'initiate').
   * This is the entry point before any catchball_pass_ball calls.
   * Idempotent: returns the existing active turn if one already exists
   * at the given org unit.
   */
  initiateTurn: (params: InitiateTurnParams) => Promise<{
    success: boolean;
    error?: string;
    turn?: CatchballTierTurn;
    idempotent?: boolean;
  }>;

  /**
   * Pass the ball one level (down = request, up = proposal/approval).
   * This is an authority action. Passing up is the approval signal.
   */
  passBall: (params: PassBallParams) => Promise<{
    success: boolean;
    error?: string;
    turn?: CatchballTierTurn;
    previousTurnId?: string;
  }>;

  /**
   * Grow the ball-tree: split a parent ball into one child ball per target org
   * unit, each with its initiate turn, as ONE server transaction
   * (catchball_fan_out, migration 756). Every target is validated first; any
   * refusal means no child was sent.
   */
  fanOut: (params: FanOutParams) => Promise<{
    success: boolean;
    error?: string;
    ballIds?: string[];
    invalidTargets?: FanOutInvalidTarget[];
  }>;

  /**
   * Ensure a root ball exists for the loop (migration 610). Idempotent —
   * returns the existing root when one is already present. Required before
   * the first fan-out when the loop has no ball_id on its active turn yet.
   */
  originateLoop: (params: OriginateLoopParams) => Promise<{
    success: boolean;
    error?: string;
    ballId?: string;
    idempotent?: boolean;
  }>;

  /**
   * Rollup: merge resolved child ball outputs into the parent (migration 620).
   * Required before parent pass-up when children exist.
   */
  aggregateBalls: (params: AggregateBallsParams) => Promise<{
    success: boolean;
    error?: string;
  }>;

  /**
   * Ball holder explicitly involves the level below in judgment work.
   * Creates a visibility record and triggers notifications for the target level.
   */
  involveLevel: (params: InvolveLevelParams) => Promise<{
    success: boolean;
    error?: string;
    involvement?: CatchballTierInvolvement;
  }>;

  /**
   * Mark a judgment prerequisite as satisfied on a turn.
   * When all required prerequisites are satisfied, the turn auto-transitions
   * to 'ready_to_pass', unlocking the pass-up action.
   */
  markPrerequisiteSatisfied: (params: MarkPrerequisiteSatisfiedParams) => Promise<{
    success: boolean;
    error?: string;
    turnState?: string;
    allRequiredMet?: boolean;
  }>;

  /**
   * For delegate-mode involvements: the target level delivers their output.
   */
  deliverInvolvement: (params: DeliverInvolvementParams) => Promise<{
    success: boolean;
    error?: string;
    involvement?: CatchballTierInvolvement;
  }>;

  // ── Realtime ──────────────────────────────────────────────────────────────

  /**
   * Subscribe to live changes on catchball_level_turns for a single loop.
   * On any insert/update/delete, reloads the loop's turns + involvements +
   * active position via loadLoop(loopId, true). Idempotent per loop; tears down
   * any previously-watched loop's channel first.
   */
  subscribeToLoopTurns: (loopId: string) => void;

  /** Tear down the active level-turn realtime channel, if any. */
  unsubscribeFromLoopTurns: () => void;
}

// ============================================================================
// Store implementation
// ============================================================================

/** Stable empty array so getActivePositions returns a referentially-stable value
 *  for unloaded loops (avoids re-render loops in zustand selectors). */
const EMPTY_ACTIVE_POSITIONS: ActiveBallPosition[] = [];

export const useCatchballTierTurnStore = create<CatchballTierTurnState>((set, get) => ({
  byLoop:  {},
  loading: {},
  error:   {},

  _realtimeChannel: null,
  _realtimeLoopId:  null,

  // ── Selectors ─────────────────────────────────────────────────────────────

  getLoopState: (loopId) => get().byLoop[loopId] ?? null,

  getActivePosition: (loopId) => get().byLoop[loopId]?.activePosition ?? null,

  getActivePositions: (loopId) => get().byLoop[loopId]?.activePositions ?? EMPTY_ACTIVE_POSITIONS,

  getTurnsForOrgUnit: (loopId, orgUnitId) =>
    (get().byLoop[loopId]?.turns ?? [])
      .filter(t => t.orgUnitId === orgUnitId)
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber),

  getActiveTurnForOrgUnit: (loopId, orgUnitId) => {
    const turns = (get().byLoop[loopId]?.turns ?? [])
      .filter(t => t.orgUnitId === orgUnitId && (t.state === 'active' || t.state === 'ready_to_pass'))
      .sort((a, b) => b.sequenceNumber - a.sequenceNumber);
    return turns[0] ?? null;
  },

  getInvolvementsForTurn: (turnId) =>
    Object.values(get().byLoop)
      .flatMap(ls => ls.involvements)
      .filter(inv => inv.tierTurnId === turnId),

  getInvolvementsTargeting: (loopId, targetOrgUnitId) =>
    (get().byLoop[loopId]?.involvements ?? [])
      .filter(inv => inv.targetOrgUnitId === targetOrgUnitId),

  isLoading: (loopId) => get().loading[loopId] ?? false,
  getError:  (loopId) => get().error[loopId] ?? null,

  // ── loadLoop ──────────────────────────────────────────────────────────────

  loadLoop: async (loopId, force = false) => {
    const state = get();
    if (!force && state.byLoop[loopId] !== undefined) return;
    if (state.loading[loopId]) return;

    set(s => ({ loading: { ...s.loading, [loopId]: true }, error: { ...s.error, [loopId]: null } }));

    if (!isSupabaseAvailable()) {
      set(s => ({
        loading: { ...s.loading, [loopId]: false },
        error:   { ...s.error,   [loopId]: 'Database unavailable' },
      }));
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set(s => ({
        loading: { ...s.loading, [loopId]: false },
        error:   { ...s.error,   [loopId]: 'No Supabase client' },
      }));
      return;
    }

    try {
      // Load turns
      const { data: turnsData, error: turnsErr } = await supabase
        .from('catchball_level_turns')
        .select('*')
        .eq('loop_id', loopId)
        .order('sequence_number', { ascending: true });

      if (turnsErr) throw new Error(turnsErr.message);

      const turns = (turnsData ?? []).map(r => dbRowToTurn(r as Record<string, unknown>));
      const turnIds = turns.map(t => t.id);

      // Load involvements for those turns
      let involvements: CatchballTierInvolvement[] = [];
      if (turnIds.length > 0) {
        const { data: invData, error: invErr } = await supabase
          .from('catchball_level_involvements')
          .select('*')
          .in('level_turn_id', turnIds)
          .order('created_at', { ascending: true });

        if (!invErr && invData) {
          involvements = invData.map(r => dbRowToInvolvement(r as Record<string, unknown>));
        }
      }

      // Load active ball position(s) per BRANCH (migration 609). Reads the
      // per-branch view so parallel sibling balls each surface their position;
      // a single-ball loop yields 0 or 1 rows. activePosition (singular) is the
      // first branch, kept for back-compat.
      let activePositions: ActiveBallPosition[] = [];
      const { data: posData, error: posErr } = await supabase
        .from('catchball_active_branch_positions')
        .select('*')
        .eq('loop_id', loopId);

      if (!posErr && posData) {
        activePositions = (posData as Record<string, unknown>[]).map(dbRowToActivePosition);
      }
      const activePosition = activePositions[0] ?? null;

      set(s => ({
        byLoop: {
          ...s.byLoop,
          [loopId]: { loopId, turns, involvements, activePosition, activePositions },
        },
        loading: { ...s.loading, [loopId]: false },
        error:   { ...s.error,   [loopId]: null },
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error loading tier turns';
      set(s => ({
        loading: { ...s.loading, [loopId]: false },
        error:   { ...s.error,   [loopId]: message },
      }));
    }
  },

  // ── initiateTurn ──────────────────────────────────────────────────────────

  initiateTurn: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_initiate_turn', {
      p_loop_id:                params.loopId,
      p_org_unit_id:            params.orgUnitId,
      p_judgment_prerequisites: params.judgmentPrerequisites ?? null,
      p_intent_content:         params.intentContent ?? null,
      p_actor_id:               params.actorId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      idempotent?: boolean;
      turn?: Record<string, unknown>;
    };

    if (!result.success) return { success: false, error: result.error };

    // Reload loop state so the new turn appears
    await get().loadLoop(params.loopId, true);

    return {
      success:    true,
      idempotent: result.idempotent ?? false,
      turn:       result.turn ? dbRowToTurn(result.turn) : undefined,
    };
  },

  // ── passBall ──────────────────────────────────────────────────────────────

  passBall: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_pass_ball', {
      p_loop_id:                params.loopId,
      p_from_org_unit_id:       params.fromOrgUnitId,
      p_to_org_unit_id:         params.toOrgUnitId,
      p_pass_direction:         params.passDirection,
      p_content:                params.content ?? null,
      p_judgment_prerequisites: params.judgmentPrerequisites ?? null,
      p_actor_id:               params.actorId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      turn?: Record<string, unknown>;
      previous_turn_id?: string;
    };

    if (!result.success) return { success: false, error: result.error };

    // One call does the whole hand-off: the pass RPC inserts the new turn;
    // 665's trigger clears the passer's claim so the receiving stop can Take
    // the ball; 850's trigger tells the people at that stop and, on a pass
    // down, opens their "Take the ball" task.
    await get().loadLoop(params.loopId, true);
    if (params.cycleId) {
      void useCatchballLoopStore.getState().fetchFromSupabase(params.cycleId);
    }

    const turn = result.turn ? dbRowToTurn(result.turn) : undefined;

    return {
      success:        true,
      turn,
      previousTurnId: result.previous_turn_id,
    };
  },

  // ── fanOut ────────────────────────────────────────────────────────────────

  fanOut: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_fan_out', {
      p_loop_id:                params.loopId,
      p_parent_ball_id:         params.parentBallId,
      p_targets:                params.targets.map((t) => ({
        org_unit_id:     t.recipientOrgUnitId,
        org_unit_name:   t.recipientOrgUnitName ?? null,
        request_payload: t.requestPayload ?? null,
      })),
      p_judgment_prerequisites: params.judgmentPrerequisites ?? [],
    });

    if (error) return { success: false, error: error.message };

    const result = (data ?? {}) as {
      success?: boolean;
      error?: string;
      invalid_targets?: FanOutInvalidTarget[];
      children?: Array<{ ball?: { id?: string } }>;
    };
    if (!result.success) {
      return {
        success: false,
        error: result.error ?? 'The server refused the fan-out. Nothing was sent.',
        invalidTargets: result.invalid_targets,
      };
    }

    // Reload the loop's branch state so the new sibling balls + their turns appear.
    await get().loadLoop(params.loopId, true);
    await useCatchballBallStore.getState().fetchBallsForLoop(params.loopId);

    return {
      success: true,
      ballIds: (result.children ?? []).map((c) => c.ball?.id).filter((id): id is string => typeof id === 'string'),
    };
  },

  // ── originateLoop ─────────────────────────────────────────────────────────

  originateLoop: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_originate_loop', {
      p_loop_id:                  params.loopId,
      p_originator_org_unit_id:   params.originatorOrgUnitId,
      p_originator_org_unit_name: params.originatorOrgUnitName ?? null,
      p_request_payload:          params.requestPayload ?? null,
      p_actor_id:                 params.actorId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      idempotent?: boolean;
      ball?: Record<string, unknown>;
    };
    if (!result.success) return { success: false, error: result.error };

    await get().loadLoop(params.loopId, true);
    await useCatchballBallStore.getState().fetchBallsForLoop(params.loopId);

    return {
      success: true,
      ballId: result.ball?.id as string | undefined,
      idempotent: result.idempotent,
    };
  },

  // ── aggregateBalls ────────────────────────────────────────────────────────

  aggregateBalls: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_aggregate_balls', {
      p_parent_ball_id: params.parentBallId,
      p_merged_content: params.mergedContent ?? null,
      p_actor_id: params.actorId,
    });
    if (error) return { success: false, error: error.message };

    const result = data as { success: boolean; error?: string; ball?: Record<string, unknown> };
    if (!result.success) return { success: false, error: result.error };

    const loopId = result.ball?.loop_id as string | undefined;
    if (loopId) {
      await get().loadLoop(loopId, true);
      await useCatchballBallStore.getState().fetchBallsForLoop(loopId);
    }
    return { success: true };
  },

  // ── involveLevel ──────────────────────────────────────────────────────────

  involveLevel: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_involve_level', {
      p_loop_id:             params.loopId,
      p_holder_org_unit_id:  params.holderOrgUnitId,
      p_target_org_unit_id:  params.targetOrgUnitId,
      p_involvement_mode:    params.involvementMode,
      p_request_content:     params.requestContent ?? null,
      p_actor_id:            params.actorId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      involvement?: Record<string, unknown>;
    };

    if (!result.success) return { success: false, error: result.error };

    const involvement = result.involvement
      ? dbRowToInvolvement(result.involvement)
      : undefined;

    // Optimistically add to store; reload will correct
    if (involvement) {
      set(s => {
        const loopState = s.byLoop[params.loopId];
        if (!loopState) return s;
        return {
          byLoop: {
            ...s.byLoop,
            [params.loopId]: {
              ...loopState,
              involvements: [...loopState.involvements, involvement],
            },
          },
        };
      });

      // The people at the involved org unit are told by the database (850).
    }

    return { success: true, involvement };
  },

  // ── markPrerequisiteSatisfied ─────────────────────────────────────────────

  markPrerequisiteSatisfied: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_mark_prerequisite_satisfied', {
      p_turn_id:      params.turnId,
      p_prereq_code:  params.prereqCode,
      p_artifact_ref: params.artifactRef ?? null,
      p_actor_id:     params.actorId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      turn_state?: string;
      all_required_met?: boolean;
    };

    if (!result.success) return { success: false, error: result.error };

    // Find which loop this turn belongs to and reload
    const loopId = Object.keys(get().byLoop).find(lid =>
      get().byLoop[lid]?.turns.some(t => t.id === params.turnId)
    );
    if (loopId) {
      await get().loadLoop(loopId, true);
    }

    return {
      success:        true,
      turnState:      result.turn_state,
      allRequiredMet: result.all_required_met,
    };
  },

  // ── deliverInvolvement ────────────────────────────────────────────────────

  deliverInvolvement: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_deliver_involvement', {
      p_involvement_id: params.involvementId,
      p_output_content: params.outputContent,
      p_actor_id:       params.actorId,
    });

    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      involvement?: Record<string, unknown>;
    };

    if (!result.success) return { success: false, error: result.error };

    const involvement = result.involvement
      ? dbRowToInvolvement(result.involvement)
      : undefined;

    // Update in place
    if (involvement) {
      set(s => {
        const loopState = s.byLoop[params.involvementId] ?? 
          Object.values(s.byLoop).find(ls =>
            ls.involvements.some(inv => inv.id === params.involvementId)
          );
        if (!loopState) return s;
        return {
          byLoop: {
            ...s.byLoop,
            [loopState.loopId]: {
              ...loopState,
              involvements: loopState.involvements.map(inv =>
                inv.id === params.involvementId ? involvement : inv
              ),
            },
          },
        };
      });
    }

    return { success: true, involvement };
  },

  // ── fetchActiveForOrgUnit ─────────────────────────────────────────────────

  fetchActiveForOrgUnit: async (orgUnitId) => {
    if (!isSupabaseAvailable()) return [];
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];

    // Query turns where this org unit is active, joined with the loop's cycle_id
    const { data, error } = await supabase
      .from('catchball_level_turns')
      .select('*, catchball_loops!inner(cycle_id)')
      .eq('org_unit_id', orgUnitId)
      .in('state', ['active', 'ready_to_pass']);

    if (error || !data) return [];

    // Ensure each loop is loaded into the store so callers can read full state
    const results: Array<{ loopId: string; cycleId: string; turn: CatchballTierTurn }> = [];
    const loopIds = [...new Set((data as Record<string, unknown>[]).map(r => r.loop_id as string))];

    await Promise.all(loopIds.map(lid => get().loadLoop(lid)));

    for (const row of data as Record<string, unknown>[]) {
      const turn = dbRowToTurn(row);
      const loopRow = row.catchball_loops as Record<string, unknown> | null;
      const cycleId = (loopRow?.cycle_id as string | undefined) ?? '';
      results.push({ loopId: turn.loopId, cycleId, turn });
    }

    return results;
  },

  // ── fetchActiveForUser ────────────────────────────────────────────────────

  fetchActiveForUser: async (userId) => {
    if (!isSupabaseAvailable()) return [];
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];

    // Resolve ALL org units the user belongs to (not just the primary one)
    let orgUnitIds: string[] = [];
    try {
      const { data: assignments } = await supabase
        .from('user_node_assignments')
        .select('org_unit_id')
        .eq('user_id', userId)
        .eq('active', true);
      orgUnitIds = (assignments ?? []).map(
        (r: { org_unit_id: string }) => r.org_unit_id,
      );
    } catch {
      return [];
    }

    if (orgUnitIds.length === 0) return [];

    const { data, error } = await supabase
      .from('catchball_level_turns')
      .select('*, catchball_loops!inner(cycle_id)')
      .in('org_unit_id', orgUnitIds)
      .in('state', ['active', 'ready_to_pass']);

    if (error || !data) return [];

    const results: Array<{ loopId: string; cycleId: string; turn: CatchballTierTurn }> = [];
    const loopIds = [...new Set((data as Record<string, unknown>[]).map(r => r.loop_id as string))];

    await Promise.all(loopIds.map(lid => get().loadLoop(lid)));

    for (const row of data as Record<string, unknown>[]) {
      const turn = dbRowToTurn(row);
      const loopRow = row.catchball_loops as Record<string, unknown> | null;
      const cycleId = (loopRow?.cycle_id as string | undefined) ?? '';
      results.push({ loopId: turn.loopId, cycleId, turn });
    }

    return results;
  },

  // ── subscribeToLoopTurns ──────────────────────────────────────────────────

  subscribeToLoopTurns: (loopId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !isSupabaseAvailable() || !loopId) return;

    const { _realtimeChannel, _realtimeLoopId } = get();
    if (_realtimeLoopId === loopId && _realtimeChannel) return; // idempotent per loop
    if (_realtimeChannel) {
      void supabase.removeChannel(_realtimeChannel); // tear down previously-watched loop
    }

    const channel = supabase
      .channel(`catchball-turns:${loopId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT / UPDATE / DELETE
          schema: 'public',
          table: 'catchball_level_turns',
          filter: `loop_id=eq.${loopId}`,
        },
        () => {
          // The active position is a DISTINCT-ON view over the turns, and
          // involvements hang off turn ids, so any turn change can shift both.
          // Reload the whole loop slice authoritatively rather than patching.
          void get().loadLoop(loopId, true);
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[CatchballTierTurnStore] Realtime channel degraded:', status);
        }
      });

    set({ _realtimeChannel: channel, _realtimeLoopId: loopId });
  },

  // ── unsubscribeFromLoopTurns ──────────────────────────────────────────────

  unsubscribeFromLoopTurns: () => {
    const supabase = getSafeSupabaseClient();
    const { _realtimeChannel } = get();
    if (_realtimeChannel && supabase) {
      void supabase.removeChannel(_realtimeChannel);
    }
    set({ _realtimeChannel: null, _realtimeLoopId: null });
  },
}));
